import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { MissionStatement } from '../../../api/models/MissionStatement';
import { UploadResult } from '../../../api/models/UploadResult';
import { routes } from '../../../route-list';
import { useRequest } from '../../../util/use-request';
import { MissionStatementFormScreen } from '../components/MissionStatementFormScreen';
import { MissionStatementFormState, missionStatementInitialFormState } from '../mission-statement-form.helper';

/**
 * Why is this not following the same structure as (link below)?
 * https://github.com/smashingboxes/arno/blob/dev/src/modules/home/containers/HomeContainer.tsx
 *
 * Reason 1: Don’t call Hooks inside loops, conditions, or nested functions
 * (https://reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level)
 * Reason 2: Don’t call Hooks from regular JavaScript functions.
 * (https://reactjs.org/docs/hooks-rules.html#only-call-hooks-from-react-functions)
 */
export const EditMissionStatementContainer = () => {
  const history = useHistory();
  const request = useRequest();
  const { id } = useParams<{ id: string }>();

  const [formState, setFormState] = useState<MissionStatementFormState>(missionStatementInitialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [didInit, setDidInit] = useState(false);

  useEffect(() => {
    if (!didInit) {
      setDidInit(true);

      (async () => {
        try {
          setIsLoading(true);
          const result = await request<MissionStatement>(apiConfigs.getMissionStatementById(id));
          setFormState({
            markdown: result.content,
            thunbnailUrl: result.imageUrl,
            thumbnail: null,
          });
        } catch (e) {
          toast.error('Failed to load mission statement!');
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [didInit, id, request]);

  return (
    <MissionStatementFormScreen
      mode="edit"
      formState={formState}
      isLoading={isLoading}
      userUpdatedFormState={setFormState}
      userClickedSaveSection={async () => {
        try {
          if (!formState.markdown) {
            toast.error('Content must not be empty!');
            return;
          }

          setIsLoading(true);

          const payload: Partial<MissionStatement> = {
            content: formState.markdown,
            imageUrl: formState.thunbnailUrl || '',
          };

          if (formState.thumbnail) {
            const upload = await request<UploadResult>(apiConfigs.uploadImage(formState.thumbnail));
            payload.imageUrl = upload.filePath;
          }

          await request(apiConfigs.updateMissionStatement(id, payload));
          toast.success('Mission statement updated!');
          history.push(routes.MISSION_STATEMENT);
        } catch (error) {
        } finally {
          setIsLoading(false);
        }
      }}
      userClickedCancel={() => {
        history.push(routes.MISSION_STATEMENT);
      }}
    />
  );
};
